import { AppWebsocketPingator } from '@/shared/features/websockets/classes/AppWebsocketPingator';
import { WSG_BASE_URL } from '@/shared/features/websockets/config/config';
import { wsLog } from '@/shared/features/websockets/helpers/log';
import {
  WebsocketCreatePayload,
  WSCloseCodes,
  WSConnectionClosedEventDetail,
  WSConnectionErrorEventDetail,
  WSConnectionOpenEventDetail,
  WSMessage,
  WSMessageEventDetail,
  WSReadyState,
  WSStatusChangeEventDetail,
} from '@/shared/features/websockets/types/websockets';
import { msg } from '@/shared/helpers/msg';
import {
  WS_CONNECTION_CHANGE,
  WS_CONNECTION_CLOSE,
  WS_CONNECTION_ERROR,
  WS_CONNECTION_OPEN,
  WS_MESSAGE_RECEIVED,
} from '@/shared/features/websockets/events';

export class AppWebsocketConnection {
  protected connection: WebSocket | null;
  protected path: string;
  protected pingator: AppWebsocketPingator | null;

  constructor(path: string) {
    this.path = path;
    this.pingator = null;
    this.connection = null;
  }

  public create({ token, notify }: WebsocketCreatePayload) {
    wsLog('👋 connecting to wsg', this.path);

    this.connection = new WebSocket(`${WSG_BASE_URL}/${this.path}?accessToken=${token}`);

    this.pingator = new AppWebsocketPingator(this.connection, this.dispatchStatusChangeEvent);

    this.dispatchStatusChangeEvent();

    this.connection.onerror = (e: Event) => {
      wsLog(`Error connection ${this.path}`);

      this.dispatchStatusChangeEvent();

      this.dispatchConnectionErrorEvent(e);
    };

    this.connection.onopen = (event) => {
      wsLog('🟢 opened');

      if (!this.pingator) throw new Error('Invalid invocation');
      this.pingator.reset();

      this.dispatchStatusChangeEvent();

      this.dispatchConnectionOpenEvent(event);

      return notify && msg.info('Connected'); // show notification
    };

    this.connection.onmessage = (e) => {
      const { event } = JSON.parse(e.data) as WSMessage<unknown>;
      //
      // Received Pong
      if (event.name === 'Pong') {
        // Pong received -> healthCheck -> success
        if (!this.pingator) throw new Error('Invalid invocation!');
        this.pingator.stopHealthCheck();
        return;
      }

      this.dispatchMessageEvent(e);
    };

    this.connection.onclose = (e) => {
      if (!this.pingator) throw new Error('Invalid invocation.');
      this.dispatchStatusChangeEvent();

      wsLog(`code: ${e.code} reason: ${e.reason || 'silent'}`);

      // if (e.wasClean) {
      //   return wsLog(`⚫️ closed clean ${this.path}`);
      // }

      wsLog(`🔴 close connection ${this.path}`);

      this.pingator.destroy();

      this.dispatchConnectionClosedEvent(e);
    };
  }

  public close(code: WSCloseCodes, reason = '') {
    if (!this.connection) return;
    wsLog('closed', this.path, reason);
    this.connection.close(code);
    if (reason) return msg.open({ content: reason, key: 'ws', type: 'error' }); // key prevents multiple notifications
  }

  protected dispatchStatusChangeEvent() {
    if (!this.connection) throw new Error('Invalid method invocation');
    const socketStatusChanged = new CustomEvent<WSStatusChangeEventDetail>(WS_CONNECTION_CHANGE, {
      bubbles: true,
      detail: {
        path: this.path,
        status: this.connection.readyState || WSReadyState.Closed,
      },
    });

    document.dispatchEvent(socketStatusChanged);
  }

  protected dispatchMessageEvent(e: MessageEvent) {
    if (!this.connection) throw new Error('Invalid method invocation');
    const messageReceived = new CustomEvent<WSMessageEventDetail>(WS_MESSAGE_RECEIVED, {
      bubbles: true,
      detail: {
        path: this.path,
        data: e,
      },
    });

    document.dispatchEvent(messageReceived);
  }

  protected dispatchConnectionErrorEvent(e: Event) {
    const connectionErrorEvent = new CustomEvent<WSConnectionErrorEventDetail>(WS_CONNECTION_ERROR, {
      bubbles: true,
      detail: {
        path: this.path,
        data: e,
      },
    });

    document.dispatchEvent(connectionErrorEvent);
  }

  protected dispatchConnectionClosedEvent(e: CloseEvent) {
    const connectionCloseEvent = new CustomEvent<WSConnectionClosedEventDetail>(WS_CONNECTION_CLOSE, {
      bubbles: true,
      detail: {
        path: this.path,
        data: e,
      },
    });

    document.dispatchEvent(connectionCloseEvent);
  }

  protected dispatchConnectionOpenEvent(e: Event) {
    const connectionOpenEvent = new CustomEvent<WSConnectionOpenEventDetail>(WS_CONNECTION_OPEN, {
      bubbles: true,
      detail: {
        path: this.path,
        data: e,
      },
    });

    document.dispatchEvent(connectionOpenEvent);
  }
  get readyState() {
    return this.connection?.readyState;
  }
}
